// ── Project Cases Section Editor ───────────────────────────────────────────────
// Each case: project name, client, description, highlights, images, partner logos

import { Plus, Trash2, GripVertical, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import ImageUploader from './ImageUploader';
import { uid } from '../../lib/uid';


export default function ProjectCasesSection({ data, onChange }) {
  const cases = data || [];

  const addCase = () => {
    const newCase = {
      id: uid(),
      project_name: '',
      client_name: '',
      description: '',
      highlights: [],
      images: [],
      partner_logos: [],
    };
    onChange([...cases, newCase]);
  };

  const removeCase = (idx) => {
    const updated = [...cases];
    updated.splice(idx, 1);
    onChange(updated);
  };

  const updateCase = (idx, field, value) => {
    const updated = [...cases];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange(updated);
  };

  const moveCase = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= cases.length) return;
    const updated = [...cases];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    onChange(updated);
  };

  // ── Image management for a case ────────────────────────────────────────
  const addImage = (caseIdx) => {
    const updated = [...cases];
    const images = [...(updated[caseIdx].images || []), { id: uid(), url: '', source_type: 'placeholder', caption: '' }];
    updated[caseIdx] = { ...updated[caseIdx], images };
    onChange(updated);
  };

  // patch can be a { field, value } pair OR a plain object of multiple fields
  const updateImage = (caseIdx, imgIdx, fieldOrPatch, value) => {
    const updated = [...cases];
    const images = [...updated[caseIdx].images];
    const patch = typeof fieldOrPatch === 'string'
      ? { [fieldOrPatch]: value }
      : fieldOrPatch;
    images[imgIdx] = { ...images[imgIdx], ...patch };
    updated[caseIdx] = { ...updated[caseIdx], images };
    onChange(updated);
  };

  const removeImage = (caseIdx, imgIdx) => {
    const updated = [...cases];
    const images = [...updated[caseIdx].images];
    images.splice(imgIdx, 1);
    updated[caseIdx] = { ...updated[caseIdx], images };
    onChange(updated);
  };

  // ── Highlight management for a case ────────────────────────────────────
  const addHighlight = (caseIdx) => {
    const updated = [...cases];
    const highlights = [...(updated[caseIdx].highlights || []), ''];
    updated[caseIdx] = { ...updated[caseIdx], highlights };
    onChange(updated);
  };

  const updateHighlight = (caseIdx, hIdx, value) => {
    const updated = [...cases];
    const highlights = [...updated[caseIdx].highlights];
    highlights[hIdx] = value;
    updated[caseIdx] = { ...updated[caseIdx], highlights };
    onChange(updated);
  };

  const removeHighlight = (caseIdx, hIdx) => {
    const updated = [...cases];
    const highlights = [...updated[caseIdx].highlights];
    highlights.splice(hIdx, 1);
    updated[caseIdx] = { ...updated[caseIdx], highlights };
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {cases.map((c, idx) => (
        <div key={c.id} className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e5e4df]">
          {/* Case header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#1a1a2e] text-white text-xs font-bold flex items-center justify-center">
                {idx + 1}
              </span>
              <span className="text-sm font-semibold text-[#1a1a2e]">
                {c.project_name || `Case #${idx + 1}`}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => moveCase(idx, -1)}
                disabled={idx === 0}
                className="p-1 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] disabled:opacity-30 transition-colors"
                title="Move up"
              >
                <GripVertical className="w-3.5 h-3.5 rotate-180" />
              </button>
              <button
                type="button"
                onClick={() => moveCase(idx, 1)}
                disabled={idx === cases.length - 1}
                className="p-1 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] disabled:opacity-30 transition-colors"
                title="Move down"
              >
                <GripVertical className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => removeCase(idx)}
                className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                title="Remove case"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Project Name */}
          <div className="mb-3">
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={c.project_name || ''}
              onChange={(e) => updateCase(idx, 'project_name', e.target.value)}
              placeholder="e.g. T1 Sydney Airport"
              className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
            />
          </div>

          {/* Client Name */}
          <div className="mb-3">
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
              Client Name
            </label>
            <input
              type="text"
              value={c.client_name || ''}
              onChange={(e) => updateCase(idx, 'client_name', e.target.value)}
              placeholder="e.g. Sydney Airport Corporation"
              className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
            />
          </div>

          {/* Description */}
          <div className="mb-3">
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
              Description
            </label>
            <textarea
              value={c.description || ''}
              onChange={(e) => updateCase(idx, 'description', e.target.value)}
              placeholder="Describe the project scope and achievements..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
            />
          </div>

          {/* Highlights */}
          <div className="mb-3">
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
              Key Highlights
            </label>
            {(c.highlights || []).map((h, hIdx) => (
              <div key={hIdx} className="flex items-center gap-2 mb-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] flex-shrink-0" />
                <input
                  type="text"
                  value={h}
                  onChange={(e) => updateHighlight(idx, hIdx, e.target.value)}
                  placeholder="e.g. Delivered on time"
                  className="flex-1 px-2 py-1.5 text-sm border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
                />
                <button
                  type="button"
                  onClick={() => removeHighlight(idx, hIdx)}
                  className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addHighlight(idx)}
              className="text-xs text-[#c9a84c] hover:text-[#b8973e] font-medium mt-1"
            >
              + Add highlight
            </button>
          </div>

          {/* Images */}
          <div>
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-2">
              Project Images
            </label>
            {(c.images || []).length > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-3">
                {(c.images || []).map((img, imgIdx) => (
                  <div key={img.id} className="relative">
                    <ImageUploader
                      value={img.url}
                      sourceType={img.source_type || 'placeholder'}
                      onChange={({ url, sourceType }) => {
                        updateImage(idx, imgIdx, { url, source_type: sourceType });
                      }}
                      onRemove={() => removeImage(idx, imgIdx)}
                      aspectRatio="16:9"
                      height="120px"
                      compact
                    />
                    <input
                      type="text"
                      value={img.caption || ''}
                      onChange={(e) => updateImage(idx, imgIdx, 'caption', e.target.value)}
                      placeholder="Caption"
                      className="w-full mt-1 px-2 py-1 text-xs border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
                    />
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => addImage(idx)}
              className="flex items-center gap-1 text-xs text-[#c9a84c] hover:text-[#b8973e] font-medium"
            >
              <ImageIcon className="w-3.5 h-3.5" />
              Add image
            </button>
          </div>
        </div>
      ))}

      {/* Add case button */}
      <Button
        type="button"
        onClick={addCase}
        size="sm"
        className="w-full border border-dashed border-[#c9a84c]/40 bg-transparent text-[#c9a84c] hover:bg-[#c9a84c]/10 hover:text-[#b8973e]"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add Project Case
      </Button>
    </div>
  );
}
